<template>
  <div class="bg-white rounded-xl shadow-sm p-6">
    <div class="flex items-center justify-between mb-4 flex-wrap gap-3">
      <div>
        <h2 class="text-lg font-bold text-neutral-900">智能体审核</h2>
        <p class="text-xs text-neutral-500 mt-1">
          教师提交发布申请后在这里审核，通过后才会在「AI 应用广场」展示和访问
        </p>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-model="status"
          class="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm outline-none"
          @change="getList"
        >
          <option :value="1">待审核</option>
          <option :value="2">已通过</option>
          <option :value="3">已驳回</option>
          <option :value="-1">全部</option>
        </select>
        <input
          v-model="keyword"
          placeholder="搜索智能体名称"
          class="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm outline-none"
          @input="handleSearch"
        />
      </div>
    </div>

    <div v-if="loading" class="text-center py-12 text-neutral-400 text-sm">加载中…</div>
    <div v-else-if="!list.length" class="text-center py-12 text-neutral-400 text-sm">
      {{ status === 1 ? "没有待审核的智能体" : "暂无数据" }}
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="a in list"
        :key="a.id"
        class="border border-neutral-200 rounded-xl p-4 flex items-start gap-4"
      >
        <div
          class="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
          :style="{ background: colorOf(a.id) }"
        >
          <img v-if="a.avatar" :src="a.avatar" class="w-full h-full object-cover" alt="" />
          <span v-else>{{ (a.name || "智")[0] }}</span>
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-medium text-neutral-900">{{ a.name }}</span>
            <span class="text-[11px] px-2 py-0.5 rounded" :class="statusClass(a.auditStatus)">
              {{ statusText(a.auditStatus) }}
            </span>
            <span class="text-[11px] px-2 py-0.5 rounded bg-neutral-100 text-neutral-600">
              {{ a.mode === "embed" ? "外部嵌入" : "自建知识库" }}
            </span>
            <span v-if="!a.keyReady && a.mode === 'kb'" class="text-[11px] text-amber-600">
              未配置密钥
            </span>
          </div>
          <p class="text-sm text-neutral-600 mt-1 line-clamp-2">{{ a.intro || "暂无简介" }}</p>
          <p class="text-xs text-neutral-400 mt-1">
            创建者：{{ a.creatorName || "老师" }}
            <span class="mx-1">·</span>{{ fmtTime(a.updateTime) }}
            <template v-if="a.mode === 'kb'">
              <span class="mx-1">·</span>{{ a.docCount || 0 }} 篇资料 / {{ a.linkCount || 0 }} 链接
              <span class="mx-1">·</span>{{ a.model || "未配置模型" }}
            </template>
            <template v-else>
              <span class="mx-1">·</span>{{ a.embedUrl }}
            </template>
          </p>
          <p v-if="a.auditStatus === 3 && a.auditRemark" class="text-xs text-red-500 mt-1">
            驳回原因：{{ a.auditRemark }}
          </p>
          <p v-if="a.auditStatus === 2 && a.auditTime" class="text-xs text-neutral-400 mt-1">
            通过时间：{{ fmtTime(a.auditTime) }}
          </p>
        </div>

        <div class="flex flex-col gap-2 flex-shrink-0">
          <button
            class="text-xs px-3 py-1 rounded bg-neutral-100 hover:bg-neutral-200"
            @click="openDetail(a)"
          >
            查看
          </button>
          <button
            v-if="a.auditStatus !== 2"
            class="text-xs px-3 py-1 rounded bg-green-500 text-white hover:bg-green-600"
            @click="approve(a)"
          >
            通过
          </button>
          <button
            v-if="a.auditStatus !== 3"
            class="text-xs px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600"
            @click="reject(a)"
          >
            驳回
          </button>
          <button
            v-if="a.auditStatus === 2"
            class="text-xs px-3 py-1 rounded bg-neutral-100 hover:bg-neutral-200 text-amber-600"
            @click="offline(a)"
          >
            下架
          </button>
        </div>
      </div>
    </div>

    <el-dialog v-model="detailVisible" :title="detail ? detail.name : ''" width="640px">
      <div v-if="detail" class="space-y-3 text-sm">
        <div class="grid grid-cols-3 gap-y-2">
          <div class="text-neutral-500">创建者</div>
          <div class="col-span-2">{{ detail.creatorName || "老师" }}</div>
          <div class="text-neutral-500">状态</div>
          <div class="col-span-2">
            <span class="text-xs px-2 py-0.5 rounded" :class="statusClass(detail.auditStatus)">
              {{ statusText(detail.auditStatus) }}
            </span>
          </div>
          <div class="text-neutral-500">模式</div>
          <div class="col-span-2">{{ detail.mode === "embed" ? "外部嵌入" : "自建知识库" }}</div>
          <div class="text-neutral-500">简介</div>
          <div class="col-span-2">{{ detail.intro || "—" }}</div>
          <template v-if="detail.mode === 'embed'">
            <div class="text-neutral-500">嵌入地址</div>
            <div class="col-span-2 break-all text-neutral-700">{{ detail.embedUrl || "—" }}</div>
          </template>
          <template v-else>
            <div class="text-neutral-500">模型</div>
            <div class="col-span-2">{{ detail.model || "—" }}</div>
            <div class="text-neutral-500">接口地址</div>
            <div class="col-span-2 break-all">{{ detail.apiBase || "—" }}</div>
            <div class="text-neutral-500">密钥</div>
            <div class="col-span-2">{{ detail.keyReady ? detail.keyMask : "未配置" }}</div>
            <div class="text-neutral-500">角色设定</div>
            <div class="col-span-2 whitespace-pre-wrap">{{ detail.systemPrompt || "—" }}</div>
            <div class="text-neutral-500">开场白</div>
            <div class="col-span-2 whitespace-pre-wrap">{{ detail.welcome || "—" }}</div>
            <div class="text-neutral-500">资料</div>
            <div class="col-span-2">
              {{ detail.docCount || 0 }} 篇资料 · {{ detail.linkCount || 0 }} 个链接
            </div>
          </template>
          <div class="text-neutral-500">提交时间</div>
          <div class="col-span-2">{{ fmtTime(detail.updateTime) }}</div>
          <div v-if="detail.auditRemark" class="text-neutral-500">审核意见</div>
          <div v-if="detail.auditRemark" class="col-span-2 text-red-500">
            {{ detail.auditRemark }}
          </div>
        </div>
        <p class="text-xs text-neutral-400 pt-2 border-t border-neutral-100">
          提示：通过后该智能体会立即出现在「知识智能体 → 广场」，所有访客可对话（消耗创建者的模型额度）。
        </p>
      </div>
      <div class="flex justify-end gap-2 mt-5">
        <button class="px-4 py-2 rounded-lg border border-neutral-200 text-sm" @click="detailVisible = false">
          关闭
        </button>
        <button
          v-if="detail && detail.auditStatus !== 2"
          class="px-4 py-2 rounded-lg bg-green-500 text-white text-sm"
          @click="approve(detail)"
        >
          通过
        </button>
        <button
          v-if="detail && detail.auditStatus !== 3"
          class="px-4 py-2 rounded-lg bg-red-500 text-white text-sm"
          @click="reject(detail)"
        >
          驳回
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "UserAgentAudit",
  data() {
    return {
      loading: false,
      list: [],
      status: 1,
      keyword: "",
      searchTimer: null,
      detail: null,
      detailVisible: false,
    };
  },
  created() {
    this.getList();
  },
  methods: {
    fmtTime(t) {
      if (!t) return "";
      const s = String(t).replace("T", " ");
      return s.length > 16 ? s.slice(0, 16) : s;
    },
    statusText(s) {
      if (s === 1) return "待审核";
      if (s === 2) return "已通过";
      if (s === 3) return "已驳回";
      return "未提交";
    },
    statusClass(s) {
      if (s === 1) return "bg-amber-50 text-amber-600";
      if (s === 2) return "bg-green-50 text-green-700";
      if (s === 3) return "bg-red-50 text-red-600";
      return "bg-neutral-100 text-neutral-500";
    },
    colorOf(id) {
      const palette = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6"];
      let h = 0;
      const s = String(id || "");
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 9973;
      return palette[h % palette.length];
    },
    handleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.getList(), 300);
    },
    async getList() {
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "user/agent/audit/list",
          method: "GET",
          params: { status: this.status, keyword: this.keyword },
        });
        if (res.code === 200 && res.data) {
          this.list = res.data.list || [];
        } else {
          ElMessage.error(res.msg || "加载失败");
        }
      } catch (e) {
        ElMessage.error("加载失败，请稍后重试");
      }
      this.loading = false;
    },
    openDetail(a) {
      this.detail = a;
      this.detailVisible = true;
    },
    async approve(a) {
      await this.review(a, true, "");
    },
    async reject(a) {
      let remark = "";
      try {
        const { value } = await ElMessageBox.prompt("请填写驳回原因，教师会在「我的」里看到", "驳回", {
          confirmButtonText: "确定驳回",
          cancelButtonText: "取消",
          inputPattern: /\S/,
          inputErrorMessage: "驳回原因不能为空",
        });
        remark = value;
      } catch (e) {
        return;
      }
      await this.review(a, false, remark);
    },
    async offline(a) {
      try {
        await ElMessageBox.confirm("下架后广场不再展示，确定？", "下架", {
          confirmButtonText: "下架",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch (e) {
        return;
      }
      await this.review(a, false, "管理员下架");
    },
    async review(a, pass, remark) {
      try {
        const res = await this.$axios({
          url: "user/agent/audit/review",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: a.id, pass, remark }) },
        });
        if (res.code === 200) {
          ElMessage.success(pass ? "已通过，智能体已发布到广场" : "已驳回");
          this.detailVisible = false;
          await this.getList();
        } else {
          ElMessage.error(res.msg || "操作失败");
        }
      } catch (e) {
        ElMessage.error("操作失败，请稍后重试");
      }
    },
  },
};
</script>
